import { useEffect, useState } from "react";

/**
 * Single source of truth for "what kind of screen is this?".
 *
 * Before this existed the shell asked `window.innerWidth < 768` in one place,
 * `< 1024` in another and `<= 768` in a third, while the CSS used 767.98 / 810 /
 * 991.98 / 1024 / 1194 / 1366. Real iPad widths (744, 768, 810, 820, 834, 1024,
 * 1032) fell between them. Everything that decides the *shell* (sidebar vs
 * bottom bar, content margin, header height) now reads from here, and the CSS
 * keys off the `data-shell` attribute Layout sets from the same value.
 *
 *   phone   < 744px, or a phone-sized touch device held in landscape
 *   tablet  744 - 1279px   (every iPad portrait, iPad landscape up to 11")
 *   desktop >= 1280px      (desktop browsers, iPad Pro 12.9"/13" landscape)
 *
 * Widths are CSS px of the *window*, so Split View / Slide Over narrow windows
 * correctly fall into a smaller class.
 */
export const PHONE_MAX = 744;
export const DESKTOP_MIN = 1280;

// A phone held sideways is ~932px wide, which would otherwise be classed as a
// tablet and get a 64px header eating 15% of a 430px-tall screen. The device's
// short physical side tells them apart from tablets (>= ~600) without relying on
// window height, which shrinks whenever an on-screen keyboard opens on Android.
const PHONE_SHORT_SIDE_MAX = 600;
const LANDSCAPE_PHONE_MAX_WIDTH = 1000;

export const isCoarsePointer = () =>
  typeof window !== "undefined" &&
  typeof window.matchMedia === "function" &&
  window.matchMedia("(pointer: coarse)").matches;

/** Pure classifier - exported for tests. */
export function classifyViewport({ width, height, coarse = false, shortSide }) {
  const deviceShortSide = shortSide || Math.min(width, height);
  const phoneDevice = coarse && deviceShortSide < PHONE_SHORT_SIDE_MAX;
  const isPhone = width < PHONE_MAX || (phoneDevice && width < LANDSCAPE_PHONE_MAX_WIDTH);
  const isDesktop = !isPhone && width >= DESKTOP_MIN;
  const isTablet = !isPhone && !isDesktop;
  return {
    width,
    height,
    isPhone,
    isTablet,
    isDesktop,
    isPortrait: height > width,
    isCoarse: coarse,
    shell: isPhone ? "phone" : isTablet ? "tablet" : "desktop",
  };
}

const readViewport = () => {
  if (typeof window === "undefined") {
    return classifyViewport({ width: DESKTOP_MIN, height: 800 });
  }
  return classifyViewport({
    width: window.innerWidth,
    height: window.innerHeight,
    coarse: isCoarsePointer(),
    shortSide: window.screen ? Math.min(window.screen.width, window.screen.height) : undefined,
  });
};

const sameViewport = (a, b) =>
  a.width === b.width && a.height === b.height && a.isCoarse === b.isCoarse && a.shell === b.shell;

export default function useViewport() {
  const [viewport, setViewport] = useState(readViewport);

  useEffect(() => {
    let frame = null;
    const update = () => {
      frame = null;
      setViewport((prev) => {
        const next = readViewport();
        return sameViewport(prev, next) ? prev : next;
      });
    };
    const schedule = () => {
      if (frame === null) frame = window.requestAnimationFrame(update);
    };
    window.addEventListener("resize", schedule);
    window.addEventListener("orientationchange", schedule);
    update();
    return () => {
      window.removeEventListener("resize", schedule);
      window.removeEventListener("orientationchange", schedule);
      if (frame !== null) window.cancelAnimationFrame(frame);
    };
  }, []);

  return viewport;
}
